import { Injectable, Inject } from '@angular/core';
import _ from 'lodash';
import { DOCUMENT } from '@angular/common';
import environment from '@environments/environment';
import { Constants } from '@app/core/utilities/constants';
import { StringUtilities } from '@app/core/utilities/string-utilities';

/**
 * A Utility service which contains common HTTP based utilities & tasks
 */
@Injectable({
  providedIn: 'root'
})
export class HttpUtilityService {

  private httpsPrefix = 'https';

  constructor(@Inject(DOCUMENT) private document: Document) { }

  /**
   * Redirects to a specified Url
   * @param urlToRedirect The Url to redirect
   */
  public redirectToUrl(urlToRedirect: string) {
    const url = _.startsWith(urlToRedirect, this.httpsPrefix) ? urlToRedirect : `${this.httpsPrefix}://${urlToRedirect}`;
    this.document.location.href = url;
  }

  /**
   * Redirects to a specified Domain
   * @param domainToRedirect The Domain to redirect
   * @param pathAndQuery The path and Query to redirect
   */
  public redirectToDomain(domainToRedirect: string, pathAndQuery: string) {
    const publicDomainUrl = environment.siteUrl;
    let enterpriseUrl = publicDomainUrl.replace(`${this.httpsPrefix}://`, `${this.httpsPrefix}://${domainToRedirect}.`);
    enterpriseUrl = enterpriseUrl + `/${pathAndQuery}`;
    this.redirectToUrl(enterpriseUrl);
  }

  /**
   * Builds the base URL for a given domain.
   * For enterprise domains, constructs the enterprise-specific URL from environment.siteUrl.
   * For public domain users, returns the current request origin as-is.
   * @param domain The user's domain (e.g. 'enterprise1')
   */
  public buildDomainBaseUrl(domain: string): string {
    const siteUrl = environment.siteUrl.replace(/\/$/, '');
    const urlDomain = this.getDomainFromCurrentRequestUrl();
    if (domain && domain !== urlDomain) {
      return siteUrl.replace(`${this.httpsPrefix}://`, `${this.httpsPrefix}://${domain}.`);
    }
    return this.getBaseUrlFromCurrentRequestUrl();
  }

  /**
   * Gets platform domain name
   */
   public getPlatformDomainName(baseDomain: string) {
    const publicDomainUrl = environment.siteUrl;
    let platformDomain = publicDomainUrl.replace(`${this.httpsPrefix}://`, `${baseDomain}`);
    platformDomain =  platformDomain.substring(platformDomain.indexOf(environment.psTokenDomain));
    return platformDomain;
  }

  /**
   * Gets platform home url (used in accounts change password/edit profile hrefs)
   * PlatformUrl and DomainName change based on the environments
   * PlatformDomainName is accounts for public domain, enterprise for enterprise domain etc..
   * URL: https://login.testing.domain.illumina.com/platform-services-manager/ for test environments
   * URL: https://login.illumina.com/platform-services-manager/ for prod etc..
   */
  public getPlatformHomeUrl() {
    const platformDomain = environment.platformDomain;
    const platformUrl = environment.platformUrl.replace(`${this.httpsPrefix}://`, `${this.httpsPrefix}://${platformDomain}.`);
    // Stable PlatformHome Url:: https://{domainName}.{platformUrl}/platform-home/#
    const platformHomeUrl = platformUrl.replace('platform-services-manager', 'platform-home/#');
    return platformHomeUrl;
  }

  /**
   * Gets workgroup admin console (wac) url.
   * Returns wac with enterprise domain when user is on enterprise domain.
   */
  public getWorkgroupAdminConsoleUrl(isPublicDomainUser: boolean) {
    let workgroupAdminConsoleUrl = environment.workgroupAdminUrl;
    // https://git.illumina.com/BaseSpace/basespace/blob/develop/src/Cloud.Web/Controllers/AbstractNoAuthController.cs#L445
    if (!isPublicDomainUser) {
      workgroupAdminConsoleUrl = workgroupAdminConsoleUrl.replace(environment.platformDomain, this.getDomainFromCurrentRequestUrl());
    }
    return workgroupAdminConsoleUrl;
  }

  /**
   * Gets the current request url
   */
  public getCurrentRequestUrl() {
    return this.document.location.href;
  }

  /**
   * Gets the Base Url segment of the current request url
   * E.g: For Url: https://www.illumina.com/company/contact-us/, returns  https://www.illumina.com
   */
  public getBaseUrlFromCurrentRequestUrl() {
    return this.document.location.origin;
  }

  /**
   * Gets the Base Url segment of the current request url
   * E.g: For Url: https://www.illumina.com/company/contact-us/, returns  'company/contact-us/'
   */
  public getPathAndQueryFromCurrentRequestUrl() {
    return `${this.document.location.pathname}${this.document.location.search}`.substr(1);
  }


  /**
   * Gets the Base Url segment of the current request url
   * E.g: For Url: https://www.illumina.com/company/contact-us/, returns  '/company/contact-us/'
   */
  public getPathFromCurrentRequestUrl() {
    return `${this.document.location.pathname}`;
  }

  /**
   * Gets the Base Url segment of the current request url
   * E.g: For Url: https://mydomain.illumina.com/company/contact-us/, returns  'mydomain'
   */
  public getDomainFromCurrentRequestUrl(): string {
    return this.document.location.host.split('.')[0];
  }

  public getDomainFromUrl(url: string): string {
    return new URL(url).host.split('.')[0];
  }

  public getQueryParamsFromUrl(url: string): Object {
    const urlObject = new URL(url);
    const queryParams = {};
    urlObject.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    return queryParams;
  }

  public getQueryParamValue(queryString: string): any {
    try {
      const params = new URLSearchParams(this.document.location.search);
      return params.get(queryString);
    } catch {
      return null;
    }
  }

  public getRelativePathFromUrl(url: string): string {
    return new URL(url).pathname;
  }

  /**
   * Gets the base url for a given URL.
   * E.g: For Url: https://www.illumina.com/company/contact-us/, returns  https://www.illumina.com
   * @param url The Url
   */
  public getBaseUrl(url: string): string {
    const urlObj = new URL(url);
    return urlObj.origin;
  }

  public isLocalEnvironment(): boolean {
    return environment.environmentName === Constants.LocalEnvironmentName;
  }

  public getQueryParamValueFromUrl(url: string, paramName: string): string {
    const urlObj = new URL(url);
    const parsedPathname = urlObj.pathname.split('/');
    const propertyIndex = parsedPathname.findIndex((property) => property === paramName);
    if(propertyIndex < 0) {
      return null;
    } else {
      return parsedPathname[propertyIndex + 1];
    }
  }

  /**
   * 
   * @param url Full url E.g: https://basespace.illumina.com/analyses/14898884/summary?resourceId=ds.00e487a5a9ce40dd98c1cf3f7f285f66
   * @returns ['analyses', 14898884, 'summary']
   */
  public getPathSegments(url: string): any[] {
    const urlObj = new URL(url);

    // remove empty segments
    return urlObj.pathname.split('/').filter(segment => segment);
  }

  /**
   * 
   * @param relativePath Takes in analyses/14898884/summary?resourceId=ds.00e487a5a9ce40dd98c1cf3f7f285f66
   * @returns returns path segments and query param object { [analyses, 14898884, summary ], { resourceId: "ds.00e487a5a9ce40dd98c1cf3f7f285f66" } }
   */
  public getRouteSegmentsAndParamsFromRelativePath(relativePath: string) {
    const fullUrl = `${StringUtilities.trimTrailingSlash(environment.siteUrl)}/${relativePath}`;
    const queryParams = this.getQueryParamsFromUrl(fullUrl)
    const pathSegments = this.getPathSegments(fullUrl);

    return { pathSegments, queryParams }
  }

  /**
   * Gets the domain from the decoded pstoken
   * @param psToken The Ps Token.
   */
  getDomainFromPsToken(psToken: string) {
    try {
      if (!StringUtilities.isBlank(psToken)) {
        const psTokenDecodedSegments = StringUtilities.fromBase64String(psToken).split(',');
        if (psTokenDecodedSegments != null && psTokenDecodedSegments.length > 1) {
          return psTokenDecodedSegments[0];
        }
      }
    } catch (error) {
      // Do nothing. Return null.
    }
    return null;
  }

}
